/**
 * Thin typed fetch wrapper. Auth header is injected by the Service Worker —
 * callers never touch the token themselves.
 *
 * Every response is checked; non-2xx throws ApiError.
 */

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

async function parseBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export async function api<T>(
  path: string,
  init: RequestInit & { expectAuth?: boolean } = {},
): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(init.headers ?? {}),
    },
    credentials: 'omit', // No cookies — token is injected by SW
  });
  const body = await parseBody(res);
  if (!res.ok) {
    const message =
      body && typeof body === 'object' && 'error' in body && typeof body.error === 'string'
        ? body.error
        : res.statusText;
    throw new ApiError(res.status, message, body);
  }
  return body as T;
}
